import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2 } from 'lucide-react';

// Calls Photon (Komoot) geocoding API directly — free, no API key needed.
// Biases results to Australia and formats addresses in a readable way.
async function fetchAddressSuggestions(input) {
    const url = new URL('https://photon.komoot.io/api/');
    url.searchParams.set('q', `${input}, Australia`);
    url.searchParams.set('limit', '8');
    url.searchParams.set('lang', 'en');
    // Bias to Australia bounding box (roughly)
    url.searchParams.set('bbox', '113.338953078,-43.6345972634,153.569469029,-10.6681857235');

    const res = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
    });
    if (!res.ok) return [];

    const data = await res.json();
    const features = Array.isArray(data?.features) ? data.features : [];

    return features
        .map((f) => {
            const p = f?.properties || {};
            const countryCode = String(p.countrycode || '').toLowerCase();
            if (countryCode && countryCode !== 'au') return null;

            const parts = [
                [p.housenumber, p.street].filter(Boolean).join(' '),
                p.suburb || p.district,
                p.city || p.county,
                p.postcode,
                p.state,
            ].filter(Boolean);

            const description = parts.join(', ');
            return description ? { description, place_id: description } : null;
        })
        .filter(Boolean)
        // Deduplicate
        .filter((item, idx, arr) => arr.findIndex(x => x.description === item.description) === idx);
}

export default function AddressAutocomplete({ label, name, value, onChange, required = false, placeholder = "", disabled = false }) {
    const [query, setQuery] = useState(value || '');
    const [suggestions, setSuggestions] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const [noResults, setNoResults] = useState(false);
    const debounceRef = useRef(null);
    const containerRef = useRef(null);
    const abortRef = useRef(null);

    useEffect(() => {
        setQuery(value || '');
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInput = (e) => {
        const val = e.target.value;
        setQuery(val);
        onChange(name, val);
        setNoResults(false);

        clearTimeout(debounceRef.current);
        if (val.trim().length < 2) {
            setSuggestions([]);
            setShowDropdown(false);
            return;
        }

        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            setSuggestions([]);
            try {
                const results = await fetchAddressSuggestions(val);
                setSuggestions(results);
                setShowDropdown(true);
                setNoResults(results.length === 0);
            } catch (err) {
                console.error('Address lookup failed:', err);
                setNoResults(true);
            } finally {
                setLoading(false);
            }
        }, 250);
    };

    const handleSelect = (suggestion) => {
        setQuery(suggestion.description);
        onChange(name, suggestion.description);
        setSuggestions([]);
        setShowDropdown(false);
        setNoResults(false);
    };

    return (
        <div className="space-y-1" ref={containerRef}>
            {label && (
                <label className="block text-sm font-medium text-gray-700">
                    {label}{required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}
            <div className="relative">
                <div className="relative">
                    <MapPin className="absolute left-2.5 top-2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                        type="text"
                        value={query}
                        onChange={handleInput}
                        onFocus={() => suggestions.length > 0 && !disabled && setShowDropdown(true)}
                        placeholder={placeholder || "Start typing a street address..."}
                        autoComplete="off"
                        disabled={disabled}
                        className="flex h-9 w-full rounded-md border border-input bg-transparent pl-8 pr-8 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    {loading && (
                        <Loader2 className="absolute right-2.5 top-2 w-4 h-4 text-gray-400 animate-spin" />
                    )}
                </div>

                {showDropdown && suggestions.length > 0 && (
                    <ul className="absolute z-50 w-full bg-white border border-gray-200 rounded-md shadow-lg mt-1 max-h-60 overflow-auto">
                        {suggestions.map((s) => (
                            <li
                                key={s.place_id}
                                onMouseDown={() => handleSelect(s)}
                                className="flex items-start gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer"
                            >
                                <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
                                {s.description}
                            </li>
                        ))}
                    </ul>
                )}

                {showDropdown && noResults && !loading && (
                    <div className="absolute z-50 w-full bg-white border border-gray-200 rounded-md shadow-lg mt-1 px-3 py-2 text-sm text-gray-400">
                        No addresses found — you can still type it in manually.
                    </div>
                )}
            </div>
        </div>
    );
}